import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Container,
  Typography,
  TextField,
  MenuItem,
  CircularProgress,
  Button,
  Paper,
  IconButton,
} from "@mui/material";
import { Print, FilterAltOff, ExpandMore, ChevronRight, FileDownload } from "@mui/icons-material";
import HeaderInternal from "../components/Header/HeaderInternal";
import { FaqButton } from "../components/FaqButton";
import { FooterInternal } from "../components/Footer";
import { useRouter } from "../contexts/RouterContext";
import { useAuth } from "../contexts/AuthContext";
import { dreReportService, DreResponse, DreLine, DreConfigVersion } from "../services/reportsService";
import { colors, typography, pageHeader } from "../theme/designSystem";
import { filterFieldSx } from "../theme/filterStyles";
import MonthSelect from "../components/Inputs/MonthSelect";

// Demonstração do Resultado (DRE) gerencial mensal.
// Receita/descontos/repasses por competência de atendimento (agenda);
// demais linhas pelo livro accountspayable (regime de caixa).

const formatCurrency = (value: number): string =>
  value.toLocaleString("pt-BR", { style: "currency", currency: "BRL", minimumFractionDigits: 2 });

const formatPercent = (value?: number | null): string =>
  value == null ? "—" : `${value.toLocaleString("pt-BR", { maximumFractionDigits: 1 })}%`;

const formatMonthLabel = (month: string): string => {
  const [year, monthNumber] = month.split("-").map((v) => parseInt(v, 10));
  return new Date(year, monthNumber - 1, 1).toLocaleDateString("pt-BR", {
    month: "long",
    year: "numeric",
  });
};

const currentMonth = (): string => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
};

// rótulo do combo de versões: "v2 05/07/2026" (data de criação da versão)
const formatVersionLabel = (v: DreConfigVersion): string => {
  const iso = v.createdAt.endsWith("Z") || v.createdAt.includes("+") ? v.createdAt : `${v.createdAt}Z`;
  return `v${v.version} ${new Date(iso).toLocaleDateString("pt-BR")}`;
};


const filterCardSx = {
  backgroundColor: "white",
  borderRadius: "12px",
  padding: "1.5rem",
  boxShadow: "0 2px 12px rgba(0, 0, 0, 0.08)",
  border: "1px solid #e9ecef",
  mb: 3,
};

const actionButtonSx = {
  padding: "0.5rem 1rem",
  borderRadius: "4px",
  fontSize: "0.85rem",
  fontWeight: typography.fontWeight.semibold,
  textTransform: "none" as const,
  boxShadow: "none",
  backgroundColor: colors.primary,
  color: "#fff",
  "&:hover": { backgroundColor: "#029AAB", boxShadow: "none" },
  "&.Mui-disabled": { backgroundColor: "#e9ecef", color: "#adb5bd" },
};

const actionButtonGraySx = {
  ...actionButtonSx,
  backgroundColor: "#6c757d",
  "&:hover": { backgroundColor: "#5a6268", boxShadow: "none" },
};

const clearFilterButtonSx = {
  backgroundColor: "#6c757d",
  color: "white",
  width: "40px",
  height: "40px",
  borderRadius: "4px",
  transition: "all 0.2s ease",
  "&:hover": { backgroundColor: "#5a6268" },
  "&:disabled": { backgroundColor: "#e9ecef", color: "#adb5bd", opacity: 0.6 },
};

const sectionStyle = {
  background: "#fff",
  borderRadius: 2,
  border: "1px solid #e9ecef",
  p: 2,
  mb: 3,
};

const valueColor = (line: DreLine): string => {
  if (!line.isResult) return colors.textPrimary;
  return line.current >= 0 ? "#28a745" : "#dc3545";
};

const ReportDre: React.FC = () => {
  const { navigateTo } = useRouter();
  const { user } = useAuth();

  const [month, setMonth] = useState<string>(currentMonth());
  const [data, setData] = useState<DreResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [exporting, setExporting] = useState<boolean>(false);
  const [exportingAnalytical, setExportingAnalytical] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  // RN-210c: versões da configuração DRE x plano de contas; "" = vigente (default)
  const [configVersions, setConfigVersions] = useState<DreConfigVersion[]>([]);
  const [configId, setConfigId] = useState<string>("");

  const currentVersionId = configVersions[0]?.id || "";
  const hasFilterChanges =
    month !== currentMonth() || (configId !== "" && configId !== currentVersionId);

  const loadData = useCallback(async (targetMonth: string, targetConfigId?: string) => {
    if (!targetMonth) return;
    setLoading(true);
    setError("");
    try {
      const response = await dreReportService.get(targetMonth, targetConfigId);
      setData(response);
      setConfigVersions(response.configVersions || []);
      setConfigId(response.appliedConfigId || "");
    } catch {
      setError("Não foi possível carregar a DRE. Tente novamente.");
    } finally {
      setLoading(false);
    }
  }, []);

  const handleMonthChange = (value: string) => {
    setMonth(value);
    if (value) loadData(value, configId || undefined);
  };

  const handleConfigChange = (value: string) => {
    setConfigId(value);
    loadData(month, value);
  };

  const clearFilters = () => {
    const value = currentMonth();
    setMonth(value);
    setConfigId("");
    loadData(value);
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const blob = await dreReportService.exportXlsx(month, configId || undefined);
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `dre_${month}.xlsx`;
      link.click();
      URL.revokeObjectURL(url);
    } catch {
      setError("Não foi possível exportar a planilha. Tente novamente.");
    } finally {
      setExporting(false);
    }
  };

  const handleExportAnalytical = async () => {
    setExportingAnalytical(true);
    try {
      const blob = await dreReportService.exportAnalyticalXlsx(month, configId || undefined);
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `dre_analitica_${month}.xlsx`;
      link.click();
      URL.revokeObjectURL(url);
    } catch {
      setError("Não foi possível exportar o analítico. Tente novamente.");
    } finally {
      setExportingAnalytical(false);
    }
  };

  const handlePrint = () => {
    setTimeout(() => window.print(), 100);
  };

  useEffect(() => {
    loadData(currentMonth());
  }, [loadData]);

  const toggleExpanded = (key: string) =>
    setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));

  const hasRevenue = data?.lines.some((l) => l.current !== 0 || l.previous !== 0) ?? false;

  const headerCellSx = {
    fontSize: 12,
    fontWeight: typography.fontWeight.semibold,
    color: "#6c757d",
    textTransform: "uppercase" as const,
    padding: "0.5rem 0.75rem",
    whiteSpace: "nowrap" as const,
  };

  const numberCellSx = {
    fontSize: 14,
    padding: "0.5rem 0.75rem",
    textAlign: "right" as const,
    whiteSpace: "nowrap" as const,
    fontVariantNumeric: "tabular-nums",
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
      <style>{`
        @media print {
          .login-header, .login-footer-component, .no-print { display: none !important; }
          body { background: #fff !important; }
          main { margin-top: 0 !important; padding: 0 !important; background: #fff !important; }
          * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          /* tabelas largas: paisagem e sem scroll horizontal, para nada sair cortado */
          @page { size: A4 landscape; margin: 8mm; }
          [style*="overflow-x"], [style*="overflowX"] { overflow: visible !important; }
          table { min-width: 0 !important; width: 100% !important; font-size: 10px !important; }
          thead { display: table-header-group; }
          tr { break-inside: avoid; }
        }
      `}</style>

      <HeaderInternal
        showCTAButton={false}
        className="login-header"
        isLoggedIn={true}
        userEmail={user?.email || ""}
        userProfile={user?.role || ""}
        clinicName={user?.clinicName || "Clínica"}
        notificationCount={0}
        onRevalidateLogin={() => {}}
        onNotificationClick={() => {}}
        onUserClick={() => {}}
        onLogoClick={() => navigateTo("dashboard")}
      />

      <Box
        component="main"
        className="c4u-main"
      >
        <Container maxWidth={false} disableGutters>
          <Box
            sx={{
              mb: 1,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              gap: 2,
            }}
          >
            <Box sx={{ minWidth: 0, flex: "1 1 auto" }}>
              <Typography variant="h4" className="c4u-page-header__title">
                Relatórios / Demonstração do Resultado
              </Typography>
              <Typography variant="body2" className="c4u-page-header__subtitle">
                DRE gerencial do mês. Receita, descontos e repasses por competência de atendimento
                (agenda); impostos e despesas pelo livro financeiro (regime de caixa).
              </Typography>
              {configVersions.length > 0 && (
                <Typography
                  className="no-print"
                  variant="body2"
                  sx={{ fontSize: typography.fontSize.sm, color: colors.textSecondary, pb: "15px" }}
                >
                  A DRE é calculada com base na versão selecionada da configuração DRE × Plano de
                  Contas — a vigente é a padrão e pode ser alterada no filtro "Versão do plano de
                  contas". As versões são mantidas no menu Financeiro → Plano de contas.
                </Typography>
              )}
            </Box>
            <Box className="no-print">
              <FaqButton />
            </Box>
          </Box>

          {/* Filtros e ações */}
          <Paper className="no-print" elevation={0} sx={filterCardSx}>
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                gap: "0.75rem",
                alignItems: { xs: "stretch", sm: "flex-start" },
                mb: 2,
              }}
            >
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                  gap: "0.75rem",
                  flex: 1,
                  maxWidth: 480,
                }}
              >
                <MonthSelect
                  label="Mês"
                  value={month}
                  onChange={handleMonthChange}
                  sx={filterFieldSx}
                />
                {configVersions.length > 0 && (
                  <TextField
                    select
                    size="small"
                    label="Versão do plano de contas"
                    value={configId}
                    onChange={(e) => handleConfigChange(e.target.value)}
                    disabled={loading}
                    fullWidth
                    sx={filterFieldSx}
                    InputLabelProps={{ shrink: true }}
                  >
                    {configVersions.map((v) => (
                      <MenuItem key={v.id} value={v.id}>
                        {formatVersionLabel(v)}
                        {v.id === currentVersionId ? " (vigente)" : ""}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              </Box>
              <IconButton
                onClick={clearFilters}
                disabled={!hasFilterChanges || loading}
                title="Limpar filtros"
                sx={{ ...clearFilterButtonSx, flexShrink: 0, alignSelf: { xs: "flex-end", sm: "auto" } }}
              >
                <FilterAltOff fontSize="small" />
              </IconButton>
            </Box>
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                justifyContent: { sm: "flex-end" },
                flexWrap: { sm: "wrap" },
                gap: "0.5rem",
                // no celular cada ação ocupa a largura toda (alvo de toque confortável)
                "& .MuiButton-root": {
                  flexShrink: 0,
                  minHeight: 44,
                },
              }}
            >
              <Button
                variant="contained"
                startIcon={<FileDownload />}
                onClick={handleExportAnalytical}
                disabled={exportingAnalytical || loading}
                sx={actionButtonGraySx}
              >
                {exportingAnalytical ? "Exportando..." : "Exportar analítico"}
              </Button>
              <Button
                variant="contained"
                startIcon={<FileDownload />}
                onClick={handleExport}
                disabled={exporting || loading}
                sx={actionButtonGraySx}
              >
                {exporting ? "Exportando..." : "Exportar planilha"}
              </Button>
              <Button
                variant="contained"
                startIcon={<Print />}
                onClick={handlePrint}
                disabled={loading || !data}
                sx={actionButtonSx}
              >
                Imprimir
              </Button>
            </Box>
          </Paper>

          {loading && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
              <CircularProgress sx={{ color: colors.primary }} />
            </Box>
          )}

          {!loading && error && (
            <Box sx={{ ...sectionStyle, textAlign: "center", color: "#dc3545" }}>{error}</Box>
          )}

          {!loading && !error && data && !hasRevenue && (
            <Box sx={{ ...sectionStyle, textAlign: "center", color: "#6c757d" }}>
              Nenhuma movimentação encontrada para o mês selecionado.
            </Box>
          )}

          {!loading && !error && data && hasRevenue && (
            <>
              <Box sx={{ ...sectionStyle, p: 0, overflowX: "auto" }}>
                <Box
                  sx={{
                    px: 2,
                    py: 1.5,
                    borderBottom: "1px solid #e9ecef",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "baseline",
                    flexWrap: "wrap",
                    gap: 1,
                  }}
                >
                  <Typography sx={{ fontSize: 15, fontWeight: typography.fontWeight.semibold }}>
                    DRE — {formatMonthLabel(data.month)}
                  </Typography>
                  <Typography sx={{ fontSize: 12, color: "#6c757d" }}>
                    {configVersions.length > 0 && configId && (
                      <>
                        Plano de contas:{" "}
                        {formatVersionLabel(
                          configVersions.find((v) => v.id === configId) || configVersions[0]
                        )}
                        {" · "}
                      </>
                    )}
                    Comparativo: {formatMonthLabel(data.previousMonth)}
                  </Typography>
                </Box>

                <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 640 }}>
                  <thead>
                    <tr style={{ borderBottom: "1px solid #e9ecef" }}>
                      <th style={{ ...headerCellSx, textAlign: "left" } as React.CSSProperties}>
                        Linha
                      </th>
                      <th style={{ ...headerCellSx, textAlign: "right" } as React.CSSProperties}>
                        {formatMonthLabel(data.month)}
                      </th>
                      <th style={{ ...headerCellSx, textAlign: "right" } as React.CSSProperties}>
                        % Receita
                      </th>
                      <th style={{ ...headerCellSx, textAlign: "right" } as React.CSSProperties}>
                        {formatMonthLabel(data.previousMonth)}
                      </th>
                      <th style={{ ...headerCellSx, textAlign: "right" } as React.CSSProperties}>
                        Variação
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.lines.map((line) => {
                      const expandable = line.categories.length > 0;
                      const isOpen = !!expanded[line.key];
                      return (
                        <React.Fragment key={line.key}>
                          <tr
                            onClick={expandable ? () => toggleExpanded(line.key) : undefined}
                            style={{
                              borderBottom: "1px solid #f1f3f5",
                              backgroundColor: line.isResult ? "#f8f9fa" : "transparent",
                              cursor: expandable ? "pointer" : "default",
                            }}
                          >
                            <td
                              style={{
                                fontSize: 14,
                                padding: "0.5rem 0.75rem",
                                fontWeight: line.isResult
                                  ? typography.fontWeight.semibold
                                  : typography.fontWeight.normal,
                                color: colors.textPrimary,
                                display: "flex",
                                alignItems: "center",
                                gap: 4,
                              }}
                            >
                              {expandable &&
                                (isOpen ? (
                                  <ExpandMore sx={{ fontSize: 18, color: "#6c757d" }} />
                                ) : (
                                  <ChevronRight sx={{ fontSize: 18, color: "#6c757d" }} />
                                ))}
                              {!expandable && <Box component="span" sx={{ width: "18px" }} />}
                              {line.label}
                            </td>
                            <td
                              style={{
                                ...numberCellSx,
                                fontWeight: line.isResult
                                  ? typography.fontWeight.semibold
                                  : typography.fontWeight.normal,
                                color: valueColor(line),
                              } as React.CSSProperties}
                            >
                              {formatCurrency(line.current)}
                            </td>
                            <td style={{ ...numberCellSx, color: "#6c757d" } as React.CSSProperties}>
                              {formatPercent(line.percentOfRevenue)}
                            </td>
                            <td style={{ ...numberCellSx, color: "#6c757d" } as React.CSSProperties}>
                              {formatCurrency(line.previous)}
                            </td>
                            <td
                              style={{
                                ...numberCellSx,
                                color:
                                  line.variationPercent == null
                                    ? "#6c757d"
                                    : line.variationPercent >= 0
                                    ? "#28a745"
                                    : "#dc3545",
                              } as React.CSSProperties}
                            >
                              {formatPercent(line.variationPercent)}
                            </td>
                          </tr>
                          {expandable &&
                            isOpen &&
                            line.categories.map((category) => (
                              <tr
                                key={`${line.key}-${category.name}`}
                                style={{ borderBottom: "1px solid #f8f9fa" }}
                              >
                                <td
                                  style={{
                                    fontSize: 13,
                                    padding: "0.35rem 0.75rem 0.35rem 2.5rem",
                                    color: "#6c757d",
                                  }}
                                >
                                  {category.name}
                                </td>
                                <td
                                  style={{ ...numberCellSx, fontSize: 13, color: "#6c757d" } as React.CSSProperties}
                                >
                                  {formatCurrency(category.current)}
                                </td>
                                <td style={numberCellSx as React.CSSProperties} />
                                <td
                                  style={{ ...numberCellSx, fontSize: 13, color: "#adb5bd" } as React.CSSProperties}
                                >
                                  {formatCurrency(category.previous)}
                                </td>
                                <td style={numberCellSx as React.CSSProperties} />
                              </tr>
                            ))}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </Box>

              {data.warnings.length > 0 && (
                <Box sx={{ ...sectionStyle, py: 1.5 }}>
                  {data.warnings.map((warning) => (
                    <Typography key={warning} sx={{ fontSize: 12, color: "#6c757d" }}>
                      • {warning}
                    </Typography>
                  ))}
                </Box>
              )}
            </>
          )}
        </Container>
      </Box>

      <FooterInternal className="login-footer-component" />
    </Box>
  );
};

export default ReportDre;
